import { useEffect, useRef } from 'react';
import Environment from 'b2b-ui-components-lib/lib/services/environment';

import moment from 'moment';
import { CALLBACK_DELAY, CART_ID, LEAD_ID, MULTI_SITE_MODULE_ID } from 'home/view/manage-feasibility/constants';

import { formatDate, findParamFromUrl, getFromSessionStorage, filterAccounts } from 'common/utils/app.utils';

import { ADDRESS_TYPE_ENUM } from 'dedicated/components/summary/summary.constant';
import { DEDICATED_COMMON_PARAMS, USER_TYPE } from 'common/constants/app.constants';
import { MODULE_ID } from 'home/common/constants/constants';

const getRequestForOrderUpdate = (form, orderDetail, isPurchaseForm, isPOChecked, customerAccounts = [], isLoiForm) => {
  let updateOrderSchema = {};
  const panNumber = orderDetail.lead.customer.panNumber || '';
  const encryptedPanNumber = btoa(panNumber);
  updateOrderSchema['panNumber'] = encryptedPanNumber;

  if (isLoiForm) {
    setLoiDetail(updateOrderSchema, form.loiDetail);
  } else if (isPurchaseForm) {
    setPurchaseDetail(updateOrderSchema, form.purchaseDetail, isPOChecked);
  } else {
    setBillingAddress(updateOrderSchema, form.billingDetails, orderDetail, customerAccounts);
  }

  return { ...updateOrderSchema };
};

const setPurchaseDetail = (updateOrderSchema, purchaseDetail, isPOChecked) => {
  if (purchaseDetail) {
    updateOrderSchema['purchaseOrderAmount'] = isPOChecked ? purchaseDetail.purchaseOrderTotalCharges.value : '';
    updateOrderSchema['purchaseOrderDate'] = isPOChecked ? formatDate(purchaseDetail.purchaseOrderDate.value) : '';
    updateOrderSchema['purchaseOrderNumber'] = isPOChecked ? purchaseDetail.purchaseOrderNo.value : 'NA';
    updateOrderSchema['purchaseOrderExpiryDate'] = isPOChecked && purchaseDetail.purchaseOrderExpiryDate.value ? formatDate(purchaseDetail.purchaseOrderExpiryDate.value) : '';
  }
  return updateOrderSchema;
};

const setLoiDetail = (updateOrderSchema, loiDetail) => {
  if (loiDetail) {
    updateOrderSchema['loiAmount'] = loiDetail.loiAmount.value || '';
    updateOrderSchema['loiDate'] = formatDate(loiDetail.loiDate.value) || '';
    updateOrderSchema['loiNumber'] = loiDetail.loiNumber.value || 'NA';
    updateOrderSchema['loiExpiryDate'] = loiDetail.loiExpiryDate.value ? formatDate(loiDetail.loiExpiryDate.value) : '';
  }
  return updateOrderSchema;
};

export const getContactsDetails = ({ contacts }) => {
  if (Array.isArray(contacts) && contacts.length > 0) {
    const { contactId, contactType } = contacts[0];
    return { contactId, contactType };
  }
  return {};
};

const setBillingAddress = (updateOrderSchema, billingForm, orderDetail, customerAccounts = []) => {
  const isSiteLevel = billingForm?.type?.id === 'LSI';
  updateOrderSchema['crmNbaId'] = billingForm?.accountNumber?.value?.key ?? '';
  updateOrderSchema['billingType'] = billingForm?.paymentType?.value?.key ?? '';
  updateOrderSchema['billingCycle'] = billingForm?.paymentCycle?.value?.key ?? '';

  // set `orderToBeLoggedInSiebel` in update order request ( isSiteLevel === 'LSI' then, billingForm?.billableId?.value?.key is undefined because on UI we don't show this field.
  const selectedAccount = filterAccounts(customerAccounts, findParamFromUrl('partyId', false))?.find(
    (item) => item?.parentAccountNumber === billingForm?.accountNumber?.value?.key
  );
  if (selectedAccount) {
    updateOrderSchema['orderToBeLoggedInSiebel'] = selectedAccount?.orderToBeLoggedInSiebel;
  }

  if (isSiteLevel) {
    updateOrderSchema['billingLevel'] = billingForm?.type?.id || '';
    return updateOrderSchema;
  }
  // updateOrderSchema['billingAddress'] = {};
  // if (billingForm?.selected?.id === ADDRESS_TYPE_ENUM.EXISTINGADDRESS) {
  //   updateOrderSchema['billingAddress']['addressLine1'] = billingForm?.existingAddress?.addressLine1 ?? '';
  //   updateOrderSchema['billingAddress']['city'] = billingForm?.existingAddress?.city ?? '';
  //   updateOrderSchema['billingAddress']['pinCode'] = billingForm?.existingAddress?.pinCode ?? '';
  // } else {
  //   updateOrderSchema['billingAddress']['addressLine1'] = billingForm?.address?.value ?? '';
  //   updateOrderSchema['billingAddress']['city'] = billingForm?.city?.value?.key ?? '';
  //   updateOrderSchema['billingAddress']['pinCode'] = billingForm?.pinCode?.value ?? '';
  //   updateOrderSchema['billingContactDetails'] = {
  //     email: billingForm?.email?.value ?? '',
  //     phoneNumber: billingForm?.mobileNumber?.value ?? '',
  //     firstName: billingForm?.personName?.value ?? '',
  //     ...getContactsDetails(orderDetail)
  //   };
  // }
  // updateOrderSchema['billingAddress']['officeName'] = billingForm?.companyName ?? '';
  updateOrderSchema['billingLevel'] = billingForm?.type?.id ?? '';
  updateOrderSchema['crmNbaId'] = billingForm?.accountNumber?.value?.key ?? '';
  // updateOrderSchema['billableId'] = billingForm?.billableId?.value?.key ?? '';
  updateOrderSchema['billingType'] = billingForm?.paymentType?.value?.key ?? '';
  updateOrderSchema['billingCycle'] = billingForm?.paymentCycle?.value?.key ?? '';

  return updateOrderSchema;
};

const getDate = (dateString) => {
  const dateMomentObject = moment(dateString, 'YYYY-MM-DD');
  return dateMomentObject.toDate();
};

const downloadFileURI = (data, fileName) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  // link.download = `${link.href.split("/").pop()}.xlsx`;
  link.download = fileName;
  // link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const validateFile = (type, size) => {
  const types = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

  const isTypeValid = types.includes(type) ? true : false;
  const isSizeValid = Math.ceil(size / 1024 / 1024) <= 1 ? true : false;

  return { isTypeValid, isSizeValid };
};

function useInterval(callback, delay = CALLBACK_DELAY) {
  const callbackRef = useRef();

  useEffect(() => {
    callbackRef.current = callback;
  });

  // useEffect(() => {
  //   callback();
  // }, []);

  useEffect(() => {
    function invokeCb() {
      callbackRef.current();
    }

    if (delay !== null) {
      let id = setInterval(invokeCb, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

const getFileName = (res, defaultFileName) => {
  const filename = res && res.headers && res.headers.hasOwnProperty('content-disposition') && res.headers['content-disposition'].split('filename=')[1];

  return filename || defaultFileName;
};

const getFileType = (type) => {
  if (type === 'pdf') {
    return { type: 'application/pdf' };
  }

  if (type === 'jpeg') {
    return { type: 'image/jpeg' };
  }

  if (type === 'jpg') {
    return { type: 'image/jpg' };
  }

  if (type === 'png') {
    return { type: 'image/png' };
  }

  return { type: 'application/pdf' };
};

const openInNewTab = (url) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) newWindow.opener = null;
};

const redirectToTMODashboard = () => {
  const env = Environment.getEnv();
  const tmoBaseUrl = env['dataTmoTrackOrderBasePageUrl'];
  const partyId = findParamFromUrl('partyId', false);
  let params = [];
  partyId && params.push(`partyId=${partyId}`);
  params = params.join('&');
  return `${tmoBaseUrl}?${params}`;
};

const redirectToTMO = ({ orderId }) => {
  const env = Environment.getEnv();
  const tmoBaseUrl = env['dataTmoTrackOrderBasePageUrl'];
  const redirectPage = `confirmed/${orderId}/details`;

  const partyId = findParamFromUrl('partyId', false);

  let params = [];

  partyId && params.push(`partyId=${partyId}`);

  params = params.join('&');

  openInNewTab(`${tmoBaseUrl}${redirectPage}?${params}`);
};

const scrollToRef = (ref, options = { behavior: 'smooth', block: 'start' }) => ref.current.scrollIntoView(options);

const getLeadIdAndCartId = () => {
  const leadId = getFromSessionStorage(MULTI_SITE_MODULE_ID, LEAD_ID);
  const cartId = getFromSessionStorage(MULTI_SITE_MODULE_ID, CART_ID);
  const productType = DEDICATED_COMMON_PARAMS.serviceType;
  const user = getFromSessionStorage(MODULE_ID, USER_TYPE) || 'CUSTOMER';
  return { leadId, cartId, productType, user };
};

const computeBandwidth = (maxBw) => {
  if (maxBw < 1000) {
    return `Available: ${maxBw} Mbps`;
  }

  return `Available: ${maxBw / 1000} Gbps`;
};

export {
  downloadFileURI,
  validateFile,
  useInterval,
  getFileName,
  getRequestForOrderUpdate,
  getDate,
  scrollToRef,
  getFileType,
  openInNewTab,
  redirectToTMO,
  getLeadIdAndCartId,
  redirectToTMODashboard,
  computeBandwidth
};
